import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Check, ArrowLeft, Download, Youtube, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import LessonQuiz from "@/components/LessonQuiz";
import CourseIntroVideo from "@/components/CourseIntroVideo";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/progress`),
      ]);
      setCourse(c.data);
      const mine = p.data.find((x) => x.course_id === id);
      setProgress(mine?.completed_lessons || []);
    } catch (e) {
      if (e.response?.status === 401) nav("/login");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { setShowQuiz(false); }, [current]);

  const checkout = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Erro no checkout, tenta de novo");
    }
  };

  const onQuizPassed = async () => {
    // refresh progress
    try {
      const { data } = await api.get(`/progress`);
      const mine = data.find((x) => x.course_id === id);
      setProgress(mine?.completed_lessons || []);
    } catch (err) { console.warn("[CourseDetail] progress refresh failed:", err?.message); }
  };

  if (loading) return <div className="p-10 text-white/40">Carregando...</div>;
  if (!course) return <div className="p-10 text-white/40">Curso não encontrado.</div>;

  const lesson = course.lessons?.[current];
  const isLocked = course.locked;
  const previousDone = current === 0 || progress.includes(course.lessons?.[current-1]?.id);
  const currentDone = progress.includes(lesson?.id);
  const canGoNext = currentDone && current < (course.lessons?.length - 1);
  const totalLessons = course.lessons?.length || 0;
  const courseCompleted = totalLessons > 0 && progress.length >= totalLessons && course.lessons.every(l => progress.includes(l.id));

  const downloadCert = () => {
    const token = localStorage.getItem("cria_token");
    const url = `${API}/courses/${id}/certificate`;
    // Fetch with auth then open blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `certificado-cria-${id}.pdf`;
        a.click();
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error("Erro ao gerar certificado"));
  };

  return (
    <div data-testid="course-detail" className="grid-bg min-h-[85vh]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <Link to="/cursos" data-testid="back-to-courses" className="inline-flex items-center gap-2 text-white/60 hover:text-[#CCFF00] font-manrope text-sm uppercase tracking-widest">
          <ArrowLeft size={16}/> Voltar
        </Link>
        <h1 className="mt-6 font-bebas text-5xl md:text-7xl tracking-tighter" style={{color: course.color}}>{course.title}</h1>
        <p className="mt-3 text-white/60 font-manrope">{course.tagline}</p>

        <CourseIntroVideo courseId={id} color={course.color || "#CCFF00"}/>

        {courseCompleted && (
          <div data-testid="cert-banner" className="mt-6 brutal-card brutal-shadow flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{borderColor:'#CCFF00'}}>
            <div>
              <span className="sticker">CURSO FINALIZADO</span>
              <h3 className="mt-3 font-bebas text-3xl tracking-tighter">Você merece seu certificado</h3>
              <p className="text-white/60 font-manrope text-sm mt-1">Baixa o PDF em alta resolução e posta lá no Insta.</p>
            </div>
            <button data-testid="download-cert" onClick={downloadCert} className="brutal-btn flex items-center gap-2">
              <Download size={18}/> Baixar Certificado PDF
            </button>
          </div>
        )}

        <div className="mt-10 grid md:grid-cols-12 gap-6">
          {/* Sidebar lessons */}
          <aside className="md:col-span-4 space-y-3">
            {course.lessons?.map((l, i) => {
              const done = progress.includes(l.id);
              const prevDone = i === 0 || progress.includes(course.lessons[i-1].id);
              const locked = (isLocked && i > 0) || (!isLocked && !prevDone && !done);
              return (
                <button
                  key={l.id}
                  data-testid={`lesson-item-${i}`}
                  onClick={() => (isLocked || !locked) && setCurrent(i)}
                  className={`w-full text-left brutal-card ${current===i ? "border-[#CCFF00]" : ""}`}
                  disabled={locked && !isLocked}
                  style={locked ? {opacity:0.5, cursor:'not-allowed'} : {}}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 border-2 flex items-center justify-center font-bebas ${done ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/40"}`}>
                      {locked ? <Lock size={14}/> : done ? <Check size={16}/> : (i+1)}
                    </div>
                    <div className="flex-1">
                      <div className="font-bebas text-lg leading-none">{l.title}</div>
                      {locked && <div className="text-white/40 font-manrope text-xs mt-1">{isLocked ? "Só pra assinantes" : "Complete a anterior"}</div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </aside>

          {/* Content */}
          <main className="md:col-span-8">
            {isLocked && current > 0 && user?.subscription_status === "pix_pending_review" ? (
              <div data-testid="paywall-pending" className="brutal-card brutal-shadow" style={{borderColor:'#FFCE00'}}>
                <span className="sticker">PIX EM ANÁLISE</span>
                <h2 className="font-bebas text-5xl mt-6 tracking-tighter">Quase lá, Cria</h2>
                <p className="mt-4 text-white/70 font-manrope">Teu Pix tá em análise. Assim que a gente confirmar, todas as aulas são liberadas.</p>
              </div>
            ) : isLocked && current > 0 ? (
              <div data-testid="paywall" className="brutal-card brutal-shadow-pink" style={{borderColor:'#FF0055'}}>
                <span className="sticker">PAY-WALL</span>
                <h2 className="font-bebas text-5xl mt-6 tracking-tighter">Libera Geral, Cria</h2>
                <p className="mt-4 text-white/70 font-manrope">
                  Essa aula tá trancada. Assina o CRIA por só <b>R$ 15,99/mês</b> e desbloqueia TODOS os cursos + o Coach AI 24/7.
                </p>
                <ul className="mt-6 space-y-2 text-white/80 font-manrope">
                  <li>✓ Acesso total a todos os cursos</li>
                  <li>✓ Coach AI ilimitado</li>
                  <li>✓ Cancela quando quiser</li>
                </ul>
                <Link to="/pagar/pix" data-testid="paywall-pix" className="brutal-btn w-full mt-8">
                  Pagar com Pix — R$ 15,99/mês
                </Link>
              </div>
            ) : lesson && (
              <div className="space-y-6">
                <div data-testid="lesson-content" className="brutal-card">
                  <div className="font-bebas text-xs tracking-widest text-white/50">AULA {current+1}</div>
                  <h2 className="font-bebas text-4xl md:text-5xl mt-2 tracking-tighter">{lesson.title}</h2>
                  <div className="mt-6 text-white/80 font-manrope leading-relaxed whitespace-pre-line">
                    {lesson.content}
                  </div>
                  <div className="mt-8 flex flex-wrap gap-4">
                    {lesson && (
                      <a
                        data-testid="watch-youtube"
                        href={`https://www.youtube.com/results?search_query=${encodeURIComponent((course?.title || "") + " " + lesson.title + " finanças")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="brutal-btn brutal-btn-outline inline-flex items-center gap-2"
                        style={{ borderColor: '#FF2A6D', color: '#FF2A6D' }}
                      >
                        <Youtube size={18}/> Ver aula no YouTube <ExternalLink size={14} className="opacity-60"/>
                      </a>
                    )}
                    {isLocked && (
                      <Link to="/pagar/pix" data-testid="quiz-paywall-cta" className="brutal-btn">
                        Assina pra fazer o quiz
                      </Link>
                    )}
                    {!isLocked && !currentDone && !showQuiz && (
                      <button data-testid="start-quiz" onClick={() => setShowQuiz(true)} className="brutal-btn">
                        Fazer o quiz (3 perguntas)
                      </button>
                    )}
                    {currentDone && (
                      <span data-testid="lesson-completed-badge" className="inline-flex items-center gap-2 font-bebas text-xl text-[#D4FF00]">
                        <Check size={22}/> AULA CONCLUÍDA
                      </span>
                    )}
                    {canGoNext && (
                      <button data-testid="next-lesson" onClick={() => setCurrent(current+1)} className="brutal-btn brutal-btn-outline">
                        Próxima aula →
                      </button>
                    )}
                  </div>
                </div>
                {(showQuiz || currentDone) && (
                  <LessonQuiz
                    courseId={id}
                    lessonId={lesson.id}
                    onPassed={onQuizPassed}
                  />
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
